import React, { useEffect, useState } from 'react';
import { Badge, Spinner, Table } from 'react-bootstrap';
import moment from 'moment';
import { AuthAPI } from '../../API';

/**
 * SurveyChangeLog — compact audit feed embedded inside DashboardDetails for
 * a single survey. Shows the last N changes for the parent survey AND its
 * children (backend union is applied in Controllers/ChangeLog.controller.js).
 */

type Row = {
  id: string;
  entityType: string;
  entityId: string;
  entityName: string | null;
  action: string;
  changedBy: string | null;
  changedByName: string | null;
  summary: string | null;
  createdAt: string;
};

type Props = {
  surveyId: string | null | undefined;
  limit?: number;
};

const ACTION_VARIANT: Record<string, string> = {
  create: 'success',
  update: 'info',
  status_change: 'warning',
  delete: 'danger',
};

const SurveyChangeLog: React.FC<Props> = ({ surveyId, limit = 20 }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!surveyId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    AuthAPI.changeLogsForSurvey(surveyId, limit)
      .then((data) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch((e: any) => {
        if (!cancelled) setError((e && e.message) || 'Failed to load change log');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [surveyId, limit]);

  if (!surveyId) return null;

  return (
    <div className="jumbotron bg-white p-3 border shadow-sm mt-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="mb-0">Change Log</h6>
        <a href="#/admin/change-logs" className="small">View all</a>
      </div>
      {loading && (
        <div className="d-flex align-items-center text-muted small">
          <Spinner animation="border" size="sm" className="mr-2" /> Loading…
        </div>
      )}
      {error && <div className="text-danger small">{error}</div>}
      {!loading && !error && rows.length === 0 && (
        <div className="text-muted small">No changes recorded for this survey yet.</div>
      )}
      {!loading && !error && rows.length > 0 && (
        <Table responsive size="sm" bordered className="mb-0">
          <thead className="thead-light">
            <tr>
              <th style={{ width: 140 }}>When</th>
              <th style={{ width: 140 }}>Who</th>
              <th style={{ width: 110 }}>Action</th>
              <th>Summary</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{moment(r.createdAt).format('YYYY-MM-DD HH:mm')}</td>
                <td>{r.changedByName || r.changedBy || '—'}</td>
                <td><Badge variant={ACTION_VARIANT[r.action] || 'secondary'}>{r.action}</Badge></td>
                <td style={{ overflowWrap: 'anywhere' }}>{r.summary || <span className="text-muted">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default SurveyChangeLog;
